import { DatePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Category, ProductErrors } from '@infrab4a/connect';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom, lastValueFrom, map } from 'rxjs';
import { Product, ProductControllerService } from 'src/app/admin-api';
import { ProductDiffModalComponent } from 'src/app/components/product-diff-modal/product-diff-modal.component';
import { TableColumn } from 'src/app/components/table';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { ShopProductErrorsService } from 'src/app/connect-api/api/shop/shop-product-errors.service';
import { ShopProductService } from 'src/app/connect-api/api/shop/shop-product.service';
import { ShopProduct } from 'src/app/connect-api/models/ShopProduct';
import { Role, roleAsObject, VariantDTO } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { ProductDetailsComponent } from '../../products/product-details/product-details.component';

interface VariantWithErrors {
  variant: VariantDTO;
  errors: ProductErrors[];
}

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss',
  providers: [DatePipe]
})
export class ProductDetailComponent implements OnInit {
  @Input() productId: string;

  private shopProductService = inject(ShopProductService);
  private productService = inject(ProductControllerService);
  private categoryService = inject(ShopCategoryService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private title = inject(Title);
  private dialog = inject(DialogService);
  private permissionsService = inject(NgxPermissionsService);
  private productErrorsService = inject(ShopProductErrorsService);

  categories: Category[];
  selectedTab = 0;
  product: Partial<ShopProduct>;
  productVariants: Array<VariantDTO>;
  permission = false;
  productErrors: ProductErrors[];
  variantErrors: Map<string, ProductErrors[]> = new Map();
  variantsWithErrors: VariantWithErrors[] = [];
  fileValidationMessage: string;
  reviewsCount: number;
  brandCategory: Category;

  rating = new FormControl(0);
  variantCols = [
    new TableColumn.Builder()
      .setHeader('Atributo')
      .setField('grade.title')
      .setCondition('contains')
      .setRouterLink('product/')
      .setRouterLinkFieldName('id')
      .build(),
    new TableColumn.Builder()
      .setHeader('Valor')
      .setField('grade.value')
      .setCondition('contains')
      .setRouterLink('product/')
      .setRouterLinkFieldName('id')
      .build(),
    new TableColumn('EAN', 'EAN', true, 'text', 'product/', 'id'),
    new TableColumn('SKU', 'sku', true, 'text', 'product/', 'id'),
    new TableColumn(
      'Peso (g)',
      'weight',
      true,
      'formattedNumber',
      undefined,
      undefined,
      true,
      'equals'
    ),
    new TableColumn.Builder()
      .setHeader('Preço custo')
      .setField('costPrice')
      .setCondition('gte')
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Estoque')
      .setField('stock.quantity')
      .setCondition('gte')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Última atualização')
      .setField('updatedAt')
      .setCondition('between')
      .setType('date')
      .build()
  ];
  productVariant: VariantDTO;
  categoryTree: Array<Category>;
  categoryTreeLabel = 'Sem categoria';
  subscriptionProduct: Product;

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue,
      roleAsObject(Role.Portfolio).enumValue
    ]);
    await this.findProduct();
    this.title.setTitle(
      this.product?.metadata?.title ||
        this.product?.metadata?.description ||
        this.product.name
    );
    await Promise.all([
      this.findProductVariants(),
      this.findCategories(),
      this.loadProductErrors()
    ]);
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['tab'] && !Number.isNaN(params['tab'])) {
        this.selectedTab = Number(params['tab']);
      }
    });
    LoaderService.showLoader(false);
  }

  async findProduct(): Promise<void> {
    try {
      this.shopProduct = await this.shopProductService.getProductById(
        this.productId
      );
      await Promise.all([this.loadProductErrors(), this.findBrandCategory()]);
      this.findSubscriptionProduct();
      if (this.product?.category?.id) await this.findCategoryTree();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true, 'Produto não encontrado');
    }
  }

  async findProductVariants(): Promise<void> {
    try {
      if (this.product?.hasVariants) {
        this.productVariants = (
          await this.shopProductService.findProductVariants(
            this.productId.toString()
          )
        ).reduce((variants, v) => {
          variants = variants.concat(
            v.grade.map((g) => ({ ...v, grade: g }) as VariantDTO)
          );
          return variants;
        }, []);
        if (this.productVariants.length > 0) {
          await this.loadVariantsErrors();
        }
      } else this.productVariants = [];
    } catch (error) {
      this.productVariants = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  async findCategories(): Promise<void> {
    try {
      this.categories = await this.categoryService.getCategoriesForProduct();
      this.categories.sort((c1, c2) => {
        if (
          FormUtil.semAcento(c1.name).toUpperCase() <
          FormUtil.semAcento(c2.name).toUpperCase()
        )
          return -1;
        if (
          FormUtil.semAcento(c1.name).toUpperCase() >
          FormUtil.semAcento(c2.name).toUpperCase()
        )
          return 1;
        return Number(c1.id) - Number(c2.id);
      });
    } catch (error) {
      this.categories = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  async loadProductErrors(): Promise<void> {
    try {
      if (this.productId) {
        this.productErrors =
          await this.productErrorsService.getProductErrorsById(this.productId);
      }
    } catch (error) {
      console.error('Erro ao carregar erros do produto:', error);
    }
  }

  async loadVariantsErrors(): Promise<void> {
    try {
      this.variantErrors.clear();
      this.variantsWithErrors = [];

      const errorPromises = this.productVariants.map(async (variant) => {
        if (variant.id) {
          try {
            const errors = await this.productErrorsService.getProductErrorsById(
              variant.id
            );
            if (errors && errors.length > 0) {
              this.variantErrors.set(variant.id, errors);
              this.variantsWithErrors.push({
                variant,
                errors
              });
            }
          } catch (error) {
            console.error(
              `Erro ao carregar erros da variante ${variant.id}:`,
              error
            );
          }
        }
      });

      await Promise.all(errorPromises);
    } catch (error) {
      console.error('Erro ao carregar erros das variantes:', error);
    }
  }

  confirmDeleteImage(image: string, $event: any) {
    this.confirmationService.confirm({
      target: $event.target,
      message: 'Deseja excluir a imagem?',
      header: 'Atenção',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          await firstValueFrom(
            this.shopProductService.removeImage(this.product.id, image)
          );
          await this.findProduct();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Imagem removida com sucesso'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  tabChanged(index: number) {
    this.router.navigate([`/shop-products/catalog/product/${this.productId}`], {
      queryParams: { tab: index },
      queryParamsHandling: 'merge'
    });
  }

  async findCategoryTree(): Promise<void> {
    try {
      if (this.product?.category?.id) {
        this.categoryTree = await this.categoryService.getCategoriesTree(
          this.product?.category?.id
        );
        this.categoryTreeLabel =
          this.categoryTree
            ?.reverse()
            .map(
              (c) =>
                `<a href="${window.location.origin}/shop-products/categories/category/${c.id}" class="data-link">${c.reference} ${c.name}</a>`
            )
            .toString()
            .replaceAll(',', ' > ') || 'Sem categoria';
      }
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findSubscriptionProduct() {
    if (this.product?.EAN || this.product?.sku)
      try {
        const product = await lastValueFrom(
          this.productService
            .findProductsTable({
              page: 0,
              pageSize: 1,
              sortBy: 'productId',
              sortDirection: 'desc',
              filters: [
                {
                  condition: 'equals',
                  field: this.product.EAN ? 'internalEAN' : 'externalId',
                  value: this.product.EAN || this.product.sku,
                  fieldType: 'text'
                }
              ]
            })
            .pipe(map((data) => data.result?.content[0]))
        );
        if (product)
          this.subscriptionProduct = await lastValueFrom(
            this.productService
              .findProductById(product.productId)
              .pipe(map((data) => data.result))
          );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
  }

  async syncInfo(): Promise<void> {
    this.dialog
      .open(ProductDiffModalComponent, {
        data: {
          product: this.subscriptionProduct,
          shopProduct: this.product
        },
        width: '60%',
        height: '90vh',
        header: 'Sincronizar ' + this.product.name,
        maximizable: true
      })
      .onClose.subscribe(async (success) => {
        if (success) {
          LoaderService.showLoader();
          await Promise.all([this.findProduct()]);
          this.messageService.add({
            summary: 'Sucesso',
            severity: 'success',
            detail: 'Produto sincronizado com sucesso.'
          });
          LoaderService.showLoader(false);
        }
      });
  }

  private set shopProduct(product: Partial<ShopProduct>) {
    this.product = product;
    this.rating.setValue(product.rate || 0);
  }

  async backFromVariantForm() {
    this.productVariant = undefined;
    await this.findProductVariants();
  }

  get toSync() {
    return ProductDetailsComponent.fieldsToSync(
      this.product,
      this.subscriptionProduct
    );
  }

  get toSyncString() {
    return (
      '\n' +
      this.toSync
        ?.map((f) => `- ${f.label}`)
        .toString()
        .replaceAll(',', '\n')
    );
  }

  get avgVariantsCostPrice() {
    return (
      (this.productVariants?.reduce(
        (sum, pv) => (sum += pv?.costPrice || 0),
        0
      ) || 0) / (this.productVariants?.length || 1)
    );
  }

  get hasProductErrors(): boolean {
    return !!this.productErrors?.length;
  }

  get hasVariantErrors(): boolean {
    return this.variantsWithErrors.length > 0;
  }

  get productErrorsList(): string[] {
    return this.productErrors?.map((error) => error.error) || [];
  }

  getVariantLabel(variant: VariantDTO): string {
    return `${variant.grade?.title}: ${variant.grade?.value}`;
  }

  getVariantErrorsList(variantId: string): string[] {
    return this.variantErrors.get(variantId)?.map((error) => error.error) || [];
  }

  getBrandEquityLabel(value: string): string {
    const brandEquityMap = {
      unknown: 'Desconhecido',
      low: 'Baixo',
      mid: 'Médio',
      strong: 'Forte'
    };

    return brandEquityMap[value] || value;
  }

  async findBrandCategory(): Promise<void> {
    try {
      if (this.product?.brand) {
        this.brandCategory = await this.categoryService.getCategoryByBrandName(
          this.product.brand
        );
      }
    } catch (error) {
      console.error('Erro ao buscar categoria da marca:', error);
    }
  }
}
